import { CSS, styled } from '@stitches/react'
import { FC, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import ProfileRegistry from '../../../stores/registry/ProfileRegistry'
import { ArpMediaType } from '../../../types/common'
import Avatar from '../../core/Avatar'
import Button from '../../core/Button/Button'
import { Box, Column, Row, Spacer, Text } from '../../core/Components'
import { Flex } from '../../core/Flex'
import { LinkIcon, OpenseaLogo } from '../../core/icons'
import { MediaTypes, segmentTrackRemix } from '../../core/Utils'
import { gray12, gray15, gray8 } from '../../platform/color'
import { Spinner, useRemix } from '../../platform/explore/common/utils'
import SampleInfo from '../sample/SampleInfo'
import { segmentDetailsPageButton } from '../SegmentDetailsPage'
import SongInfo from '../song/SongInfo'
import StemInfo from '../stem/StemInfo'
import CircleFavoriteButton from './CircleFavoriteButton'
import useMetatagParser from './samplePackUtils'
import TweetButton from './TweetButton'

const MediaTitle = styled('div', {
  fontFamily: 'DM Sans',
  fontStyle: 'normal',
  fontWeight: '500',
  fontSize: '32px',
  lineHeight: '42px',
  letterSpacing: '-0.02em',
  color: '#000000'
})

const MediaArtistTitle = styled('div', {
  color: gray8,
  fontFamily: 'DM Sans',
  fontStyle: 'normal',
  fontWeight: '400',
  fontSize: '20px',
  lineHeight: '26px',
  letterSpacing: '-0.02em',
  '&:hover': {
    textDecoration: 'underline'
  }
})

const HorizontalLine = styled('div', {
  height: '1px',
  width: '100%',
  backgroundColor: gray12,
  margin: '15px 0px'
})

interface MediaInfoColumnProps {
  title: string
  children: any
  flex?: 'column' | 'row'
  css?: {}
}
export const MediaInfoColumn: FC<MediaInfoColumnProps> = ({ title, children, flex, css }) => (
  <Column css={{ marginBottom: '20px', ...css }}>
    <Flex direction={flex || 'column'}>
      <Box>
        <Box css={{ fontWeight: '700', fontSize: '13px', color: gray15 }}>{title}</Box>
        <HorizontalLine />
      </Box>
      <Spacer />
      <Box css={{ flexGrow: 1 }}>{children}</Box>
    </Flex>
  </Column>
)

interface MediaExtraInfoProps {
  children: any
  css?: {}
}
export const MediaExtraInfo: FC<MediaExtraInfoProps> = ({ children, css }) => (
  <Text css={{ fontWeight: '700', fontSize: '20px', paddingBottom: '25px', ...css }}>
    {children}
  </Text>
)

interface MediaInfoTitleProps {
  title: string
  artistName: string
  artistEthAddress: string
  customSamplePackTitle?: string
  customSamplePackDescription?: string
}
export const MediaInfoTitle: FC<MediaInfoTitleProps> = ({
  title,
  artistName,
  artistEthAddress,
  customSamplePackTitle,
  customSamplePackDescription
}) => {
  const [pfp, setPFP] = useState<string>(null)

  useEffect(() => {
    ProfileRegistry.getByIndexAsync(artistEthAddress).then((profile) =>
      setPFP(profile.profile_picture)
    )
  }, [artistEthAddress])

  return (
    <>
      {customSamplePackTitle && (
        <Text>
          <Link to={`/profile/${artistEthAddress}/created/samples`}>{customSamplePackTitle}</Link>
          <LinkIcon style={{ marginLeft: '4px' }} />
        </Text>
      )}
      <Row verticalCenter css={{ gap: '25px', flexWrap: 'wrap' }}>
        <Avatar size="60px" imageUrl={pfp} />
        <Column verticalCenter>
          <MediaTitle>
            <Text
              css={{
                whiteSpace: 'break-spaces',
                marginBottom: 0,
                wordWrap: 'break-word',
                maxWidth: '100%'
              }}
              id={'animated'}
            >
              {title}
            </Text>
          </MediaTitle>
          <MediaArtistTitle>
            <Link to={`/profile/${artistEthAddress}`}>{artistName}</Link>
          </MediaArtistTitle>
        </Column>
      </Row>
      {
        // TODO Max please help me format this nicely with much smaller text, see the mock for example :D
        customSamplePackDescription && (
          <Text
            css={{ fontSize: '14px', lineHeight: '20px', marginTop: '16px', marginBottom: '-34px' }}
          >
            {customSamplePackDescription}
          </Text>
        )
      }
    </>
  )
}

const useMediaInfoButtons = (mediaType: 'song' | 'stem' | 'sample') => {
  const { remixSong, remixSample, remixStem, status } = useRemix()

  switch (mediaType) {
    case MediaTypes.SONG:
      return {
        remixFunc: remixSong,
        status
      }
    case MediaTypes.SAMPLE:
      return {
        remixFunc: remixSample,
        status
      }
    case MediaTypes.STEM:
      return {
        remixFunc: remixStem,
        status
      }
    default:
      throw new Error('Unkown media type')
  }
}

interface MediaInfoButtons {
  openseaLink: string
  index: number
  type: ArpMediaType
  css?: CSS
  favorite?: boolean
  customLink?: {
    url: string
    label: string
  }
}

export const MediaInfoButtons: React.FC<MediaInfoButtons> = ({
  openseaLink,
  index,
  type,
  favorite = true,
  css = {}
}) => {
  const { remixFunc, status } = useMediaInfoButtons(type)

  const { meta } = useMetatagParser(index, type)

  const customLink: { url: string; label: string } | undefined = meta['project-url']
    ? { url: meta['project-url'], label: meta.project }
    : undefined

  return (
    <Row css={{ marginTop: '32px', ...css }}>
      <Button
        type={'dark'}
        big
        rounded
        css={{ flexGrow: 1, '@tablet': { maxWidth: '200px' } }}
        disabled={status === 'loading'}
        onClick={() => {
          segmentTrackRemix(type, index, 'details header')
          remixFunc(index)
        }}
      >
        {status === 'loading' && <Spinner />}
        {status === 'ready' && 'Open in Studio'}
      </Button>
      {customLink && (
        <Button
          type={'outlined'}
          big
          rounded
          css={{ flexGrow: 1, marginLeft: '8px', '@tablet': { maxWidth: '200px' } }}
          onClick={() => window.open(customLink.url, '_blank')}
        >
          {customLink.label}
        </Button>
      )}
      {favorite && (
        <CircleFavoriteButton arpMediaIndex={index} mediaType={type} css={{ marginLeft: '8px' }} />
      )}
      <TweetButton
        index={index}
        mediaType={type}
        css={{ marginLeft: '8px' }}
        onClick={() => {
          segmentDetailsPageButton('Tweet Button', index, type)
        }}
      />
      {openseaLink && (
        <a href={openseaLink} target="_blank" rel="noreferrer">
          <Button
            type={'outlined'}
            bigSquare
            rounded
            css={{ marginLeft: '8px' }}
            onClick={() => {
              segmentDetailsPageButton('View on Opensea', index, type)
            }}
          >
            <OpenseaLogo />
          </Button>
        </a>
      )}
    </Row>
  )
}

interface MediaInfoProps {
  index: number
  type: string
  css?: {}
}

const MediaInfo: FC<MediaInfoProps> = ({ index, type, css }) => (
  <Row css={css}>
    {type === MediaTypes.SONGS && <SongInfo index={index} />}
    {type === MediaTypes.STEMS && <StemInfo index={index} />}
    {type === MediaTypes.SAMPLES && <SampleInfo index={index} />}
  </Row>
)

export default MediaInfo
